import React, { useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, Tabs, Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { formatMoney, getCurrency } from '../../utils/currency';
import { DENOMINATIONS, buildCurrencySummaries } from './cashDetails';

const RED = 'error.main';

const currencyTitle = (code) => `${getCurrency(code).label} (${getCurrency(code).symbol})`;
// Para las pestañas: "Pesos ($)", "Dólares (US$)".
const currencyTabTitle = (code) => `${getCurrency(code).label.split(' ')[0]} (${getCurrency(code).symbol})`;

/** Una fila de la tabla de efectivo: concepto a la izquierda y monto a la derecha. */
const Line = ({ label, value, code, bold = false, color, hint, indent = false }) => (
  <TableRow>
    <TableCell sx={{ pl: indent ? 4 : 2, fontWeight: bold ? 700 : 400, color: indent ? 'text.secondary' : undefined, borderBottom: 0, py: 0.75 }}>
      {label}
      {hint && <Typography component="span" variant="caption" color="text.secondary"> {hint}</Typography>}
    </TableCell>
    <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400, color: color || (indent ? 'text.secondary' : undefined), borderBottom: 0, py: 0.75 }}>
      {formatMoney(value, code)}
    </TableCell>
  </TableRow>
);

/** Billetes y monedas contados, sin los que no hay. */
const Count = ({ data }) => {
  const rows = DENOMINATIONS.filter((value) => Number(data?.[`dec${value}`] ?? 0) > 0).slice().reverse();
  if (!rows.length) return <Typography variant="body2" color="text.secondary">Sin conteo</Typography>;
  return rows.map((value) => <Typography key={value} variant="body2">{data[`dec${value}`]} de ${value}</Typography>);
};

const CurrencyPanel = ({ summary, openData, closeData, t }) => {
  const { code } = summary;
  const closed = summary.declared != null;
  const difference = summary.difference ?? 0;
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>Efectivo en caja</Typography>
      <Table size="small" sx={{ mb: 1 }}>
        <TableBody>
          <Line label="Al abrir la caja" value={summary.openingCash} code={code} />
          <Line label="+ Ventas en efectivo" value={summary.cashSales} code={code} />
          <Line label="− Egresos" value={summary.outflows === 0 ? 0 : -summary.outflows} code={code} />
          {summary.purchases > 0 && <Line label="Compras" value={summary.purchases} code={code} indent />}
          {summary.deposits > 0 && <Line label="Depósitos" value={summary.deposits} code={code} indent />}
          {summary.withdrawals > 0 && <Line label="Retiros" value={summary.withdrawals} code={code} indent />}
          <Line label="Debería haber" value={summary.expected} code={code} bold />
          {closed ? (
            <>
              <Line label="Declarado al cerrar" value={summary.declared} code={code} bold color={summary.mismatch ? RED : undefined} />
              <TableRow>
                <TableCell sx={{ fontWeight: 700, borderBottom: 0, py: 0.75 }}>Diferencia</TableCell>
                <TableCell align="right" sx={{ borderBottom: 0, py: 0.75 }}>
                  {summary.mismatch
                    ? <Typography component="span" sx={{ fontWeight: 700, color: RED }}>{difference > 0 ? '+' : ''}{formatMoney(difference, code)}</Typography>
                    : <Chip size="small" color="success" variant="outlined" label="Sin diferencia" />}
                </TableCell>
              </TableRow>
            </>
          ) : (
            <TableRow><TableCell colSpan={2} sx={{ borderBottom: 0 }}><Typography variant="body2" color="text.secondary">La caja todavía no se cerró.</Typography></TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>Cobrado por otros medios</Typography>
      <Table size="small" sx={{ mb: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell>Medio</TableCell>
            <TableCell align="right">Según el sistema</TableCell>
            <TableCell align="right">Declarado al cerrar</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {summary.others.map((method) => (
            <TableRow key={method.key}>
              <TableCell>{method.label}</TableCell>
              <TableCell align="right">{formatMoney(method.system, code)}</TableCell>
              <TableCell align="right" sx={{ color: method.mismatch ? RED : undefined, fontWeight: method.mismatch ? 700 : 400 }}>
                {method.declared == null ? '—' : formatMoney(method.declared, code)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {summary.isBase && (
        <Accordion disableGutters variant="outlined" sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Conteo de billetes y monedas</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Al abrir</Typography><Count data={openData} /></Grid>
              <Grid item xs={6}><Typography variant="caption" color="text.secondary">Al cerrar</Typography>{closeData ? <Count data={closeData} /> : <Typography variant="body2" color="text.secondary">—</Typography>}</Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

const CashDetailsModal = ({ open, onClose, openData, closeData }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);

  if (!openData) return null;

  const summaries = buildCurrencySummaries(openData, closeData);
  const active = summaries[Math.min(tab, summaries.length - 1)];
  const differing = summaries.filter((summary) => summary.mismatch || summary.others.some((method) => method.mismatch)).map((summary) => summary.code);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('cash_details')}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}><Typography><strong>{t('branch')}:</strong> {openData.branch.name}</Typography></Grid>
          <Grid item xs={12} sm={6}><Typography><strong>{t('terminal')}:</strong> {openData.terminal.reference}</Typography></Grid>
          <Grid item xs={12} sm={6}><Typography><strong>{t('staff')}:</strong> {openData.user}</Typography></Grid>
          <Grid item xs={12} sm={6}><Typography><strong>{t('date')}:</strong> {openData.formattedDate}</Typography></Grid>
          <Grid item xs={12} sm={6}><Typography><strong>{t('open_time')}:</strong> {openData.formattedTime}</Typography></Grid>
          {closeData && <Grid item xs={12} sm={6}><Typography><strong>{t('close_time')}:</strong> {closeData.formattedTime}</Typography></Grid>}
        </Grid>

        {!closeData && <Typography sx={{ mb: 2, color: RED }}>{t('closing_data_not_available')}</Typography>}
        {closeData?.notes && (
          <Alert severity="info" icon={false} sx={{ mb: 2, whiteSpace: 'pre-wrap' }}><strong>{t('notes')}:</strong> {closeData.notes}</Alert>
        )}

        {summaries.length > 1 && (
          <Tabs value={Math.min(tab, summaries.length - 1)} onChange={(_, value) => setTab(value)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            {summaries.map((summary) => (
              <Tab
                key={summary.code}
                label={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {currencyTabTitle(summary.code)}
                    {differing.includes(summary.code) && <Chip size="small" color="error" label="Con diferencia" />}
                  </Box>
                )}
              />
            ))}
          </Tabs>
        )}
        {summaries.length === 1 && <Typography variant="h6">{currencyTitle(active.code)}</Typography>}

        <CurrencyPanel summary={active} openData={openData} closeData={closeData} t={t} />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">{t('close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CashDetailsModal;
